import { useRef, useCallback } from 'react';
import type { Graph } from '@antv/x6';
import { safePageToLocal } from '../utils/iosCompat';
import { DRAG_THRESHOLD } from '../utils/nodeHelpers';

export interface UseRubberbandSelectionOptions {
  graph: Graph | null;
  cleanGraphSelection: () => void;
  selectGraphNode: (node: any, mode: 'single' | 'group') => void;
  onNodeSelect?: (node: any) => void;
  setStatusText: (text: string) => void;
}

export function useRubberbandSelection({
  graph,
  cleanGraphSelection,
  selectGraphNode,
  onNodeSelect,
  setStatusText,
}: UseRubberbandSelectionOptions) {
  const isRubberbandActiveRef = useRef(false);
  const isRubberbandPreparingRef = useRef(false);
  const suppressBlankClickRef = useRef(false);
  const suppressNodeClickRef = useRef(false);

  const selectionStartLocalRef = useRef<{ x: number; y: number } | null>(null);
  const selectionStartClientRef = useRef<{ x: number; y: number } | null>(null);
  const selectionCurrentLocalRef = useRef<{ x: number; y: number } | null>(null);
  const selectionCurrentClientRef = useRef<{ x: number; y: number } | null>(null);

  const selectionBoxRef = useRef<HTMLDivElement | null>(null);
  const selectionResultBoxRef = useRef<HTMLDivElement | null>(null);
  const selectionResultBoundsRef = useRef<
    { minX: number; minY: number; maxX: number; maxY: number } | null
  >(null);

  const ensureSelectionBox = useCallback(() => {
    if (!graph) return;
    if (selectionBoxRef.current) return;
    const container = graph.container?.parentElement;
    if (!container) return;
    const box = document.createElement('div');
    box.className = 'opm-x6-selection-box';
    Object.assign(box.style, {
      position: 'absolute',
      border: '1px dashed #1890ff',
      background: 'rgba(24, 144, 255, 0.12)',
      display: 'none',
      pointerEvents: 'none',
      zIndex: 9999,
    });
    container.appendChild(box);
    selectionBoxRef.current = box;
  }, [graph]);

  const ensureSelectionResultBox = useCallback(() => {
    if (!graph) return;
    if (selectionResultBoxRef.current) return;
    const container = graph.container?.parentElement;
    if (!container) return;
    const box = document.createElement('div');
    box.className = 'opm-x6-selection-box';
    Object.assign(box.style, {
      position: 'absolute',
      border: '2px dashed #1890ff',
      background: 'rgba(24, 144, 255, 0.05)',
      display: 'none',
      pointerEvents: 'none',
      zIndex: 9998,
    });
    container.appendChild(box);
    selectionResultBoxRef.current = box;
  }, [graph]);

  const hideSelectionBox = useCallback(() => {
    if (selectionBoxRef.current) {
      selectionBoxRef.current.style.display = 'none';
    }
  }, []);

  const updateSelectionBox = useCallback(() => {
    if (!graph || !selectionBoxRef.current) return;
    const startClient = selectionStartClientRef.current;
    const currentClient = selectionCurrentClientRef.current;
    if (!startClient || !currentClient) return;
    const containerRect = graph.container.getBoundingClientRect();
    const left = Math.min(startClient.x, currentClient.x) - containerRect.left;
    const top = Math.min(startClient.y, currentClient.y) - containerRect.top;
    const width = Math.abs(currentClient.x - startClient.x);
    const height = Math.abs(currentClient.y - startClient.y);
    Object.assign(selectionBoxRef.current.style, {
      display: 'block',
      left: `${left}px`,
      top: `${top}px`,
      width: `${width}px`,
      height: `${height}px`,
    });
  }, [graph]);

  const updateSelectionResultBox = useCallback(() => {
    if (!graph) return;
    const bounds = selectionResultBoundsRef.current;
    if (!bounds) return;
    ensureSelectionResultBox();
    if (!selectionResultBoxRef.current) return;
    const containerRect = graph.container.getBoundingClientRect();
    const topLeft = graph.localToClient({ x: bounds.minX, y: bounds.minY });
    const bottomRight = graph.localToClient({ x: bounds.maxX, y: bounds.maxY });
    const left = topLeft.x - containerRect.left;
    const top = topLeft.y - containerRect.top;
    const width = bottomRight.x - topLeft.x;
    const height = bottomRight.y - topLeft.y;
    Object.assign(selectionResultBoxRef.current.style, {
      display: 'block',
      left: `${left}px`,
      top: `${top}px`,
      width: `${width}px`,
      height: `${height}px`,
    });
  }, [graph, ensureSelectionResultBox]);

  const updateSelectionBoxFromNodes = useCallback((nodes: any[]) => {
    if (!nodes.length) return;
    const bounds = nodes.reduce(
      (acc, node) => {
        const bbox = node.getBBox();
        return {
          minX: Math.min(acc.minX, bbox.x),
          minY: Math.min(acc.minY, bbox.y),
          maxX: Math.max(acc.maxX, bbox.x + bbox.width),
          maxY: Math.max(acc.maxY, bbox.y + bbox.height),
        };
      },
      { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity },
    );
    selectionResultBoundsRef.current = bounds;
    updateSelectionResultBox();
  }, [updateSelectionResultBox]);

  const resetSelectionState = useCallback(() => {
    isRubberbandActiveRef.current = false;
    isRubberbandPreparingRef.current = false;
    selectionStartLocalRef.current = null;
    selectionCurrentLocalRef.current = null;
    selectionStartClientRef.current = null;
    selectionCurrentClientRef.current = null;
    hideSelectionBox();
  }, [hideSelectionBox]);

  const beginSelection = useCallback((clientX: number, clientY: number) => {
    if (!graph) return;
    selectionStartClientRef.current = { x: clientX, y: clientY };
    selectionCurrentClientRef.current = { x: clientX, y: clientY };
    selectionStartLocalRef.current = safePageToLocal(graph, clientX, clientY);
    selectionCurrentLocalRef.current = selectionStartLocalRef.current;
    isRubberbandPreparingRef.current = true;
    ensureSelectionBox();
    selectionResultBoundsRef.current = null;
    if (selectionResultBoxRef.current) {
      selectionResultBoxRef.current.style.display = 'none';
    }
    hideSelectionBox();
    graph.disablePanning();
  }, [graph, ensureSelectionBox, hideSelectionBox]);

  const handleSelectionMouseMove = useCallback((clientX: number, clientY: number) => {
    if (!graph) return;
    if (!isRubberbandPreparingRef.current && !isRubberbandActiveRef.current) return;
    selectionCurrentLocalRef.current = safePageToLocal(graph, clientX, clientY);
    selectionCurrentClientRef.current = { x: clientX, y: clientY };
    const startClient = selectionStartClientRef.current;
    if (!startClient) return;
    const dist = Math.hypot(clientX - startClient.x, clientY - startClient.y);
    if (!isRubberbandActiveRef.current && dist >= DRAG_THRESHOLD) {
      ensureSelectionBox();
      isRubberbandActiveRef.current = true;
    }
    if (isRubberbandActiveRef.current) {
      updateSelectionBox();
    }
  }, [graph, ensureSelectionBox, updateSelectionBox]);

  const handleSelectionMouseUp = useCallback((clientX: number, clientY: number): boolean => {
    if (!graph) return false;
    if (!isRubberbandActiveRef.current && !isRubberbandPreparingRef.current) return false;
    selectionCurrentLocalRef.current = safePageToLocal(graph, clientX, clientY);
    selectionCurrentClientRef.current = { x: clientX, y: clientY };

    if (isRubberbandActiveRef.current) {
      const start = selectionStartLocalRef.current;
      const current = selectionCurrentLocalRef.current;
      if (start && current) {
        const minX = Math.min(start.x, current.x);
        const maxX = Math.max(start.x, current.x);
        const minY = Math.min(start.y, current.y);
        const maxY = Math.max(start.y, current.y);
        const nodes = graph.getNodes();
        const selectedNodes = nodes.filter((node: any) => {
          const bbox = node.getBBox();
          const centerX = bbox.x + bbox.width / 2;
          const centerY = bbox.y + bbox.height / 2;
          return centerX >= minX && centerX <= maxX && centerY >= minY && centerY <= maxY;
        });
        cleanGraphSelection();
        if (selectedNodes.length) {
          selectedNodes.forEach(node => selectGraphNode(node, 'group'));
          selectionResultBoundsRef.current = { minX, minY, maxX, maxY };
          updateSelectionResultBox();
          onNodeSelect?.(null);
          setStatusText(`框选 ${selectedNodes.length} 个节点`);
        } else {
          onNodeSelect?.(null);
          setStatusText('未选中节点');
        }
      }
      suppressBlankClickRef.current = true;
      suppressNodeClickRef.current = true;
    }
    resetSelectionState();
    graph.enablePanning();
    return true;
  }, [graph, cleanGraphSelection, selectGraphNode, updateSelectionResultBox, onNodeSelect, setStatusText, resetSelectionState]);

  const cleanup = useCallback(() => {
    if (selectionBoxRef.current?.parentElement) {
      selectionBoxRef.current.parentElement.removeChild(selectionBoxRef.current);
    }
  }, []);

  return {
    isRubberbandActiveRef,
    isRubberbandPreparingRef,
    suppressBlankClickRef,
    suppressNodeClickRef,
    selectionResultBoundsRef,
    beginSelection,
    handleSelectionMouseMove,
    handleSelectionMouseUp,
    updateSelectionResultBox,
    updateSelectionBoxFromNodes,
    cleanup,
  };
}
